import React, { useState, useEffect } from 'react';
import { productAPI, orderAPI, categoryAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Plus, Package, TrendingUp, ShoppingBag, Star, Edit, Trash2, X, SlidersHorizontal } from 'lucide-react';
import toast from 'react-hot-toast';
import { AdjustStockModal } from './admin/AdminInventory';
import ImageUpload from '../components/common/ImageUpload';

const EMPTY_FORM = { name: '', description: '', price: '', mrp: '', stock: '', images: [], brand: '', category_id: '', external_url: '' };

export default function SellerPage() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('products');
  const [categories, setCategories] = useState([]);
  const [adjustProduct, setAdjustProduct] = useState(null);

  useEffect(() => {
    categoryAPI.getAll().then(r => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pRes, oRes] = await Promise.all([
        productAPI.getSellerProducts(),
        orderAPI.getAll(),
      ]);
      setProducts(pRes.data.products || []);
      setOrders(oRes.data.orders || []);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const totalRevenue = orders.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0);

  const openCreate = () => { setEditProduct(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (p) => {
    setEditProduct(p);
    setForm({ name: p.name, description: p.description || '', price: p.price, mrp: p.mrp || '', stock: p.stock || 0, images: p.images || [], brand: p.brand || '', category_id: p.category_id || '', external_url: p.external_url || '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.price) return toast.error('Name and price required');
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price), mrp: form.mrp ? parseFloat(form.mrp) : undefined };
      if (!editProduct) payload.stock = parseInt(form.stock) || 0;
      else delete payload.stock;
      if (editProduct) {
        await productAPI.update(editProduct.id, payload);
        toast.success('Product updated!');
      } else {
        await productAPI.create(payload);
        toast.success('Product added!');
      }
      setShowModal(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await productAPI.delete(id);
      toast.success('Deleted!');
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const STATUS_COLORS = { pending: '#f59e0b', confirmed: '#3b82f6', shipped: '#8b5cf6', delivered: '#22c55e', cancelled: '#ef4444' };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #f43397, #6c3fc5)', padding: '32px 24px', color: 'white' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700 }}>Seller Dashboard</h1>
          <p style={{ margin: '6px 0 0', opacity: 0.85 }}>Welcome, {user?.name}! Manage your products and orders.</p>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Total Products', value: products.length, icon: Package, color: '#f43397' },
            { label: 'Total Orders', value: orders.length, icon: ShoppingBag, color: '#6c3fc5' },
            { label: 'Revenue', value: `₹${totalRevenue.toFixed(0)}`, icon: TrendingUp, color: '#22c55e' },
            { label: 'Avg Rating', value: products.length ? (products.reduce((s, p) => s + parseFloat(p.avg_rating || 0), 0) / products.length).toFixed(1) : '—', icon: Star, color: '#f59e0b' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} style={{ background: 'white', borderRadius: 16, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={20} color={color} />
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>{value}</div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>{label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {['products', 'orders'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '10px 24px', border: 'none', borderRadius: 10, background: activeTab === tab ? '#f43397' : 'white', color: activeTab === tab ? 'white' : '#64748b', fontWeight: 600, fontSize: 14, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', textTransform: 'capitalize' }}>
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 'products' && (
          <div style={{ background: 'white', borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>My Products</h2>
              <button onClick={openCreate} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f43397', color: 'white', border: 'none', borderRadius: 10, padding: '8px 16px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                <Plus size={16} /> Add Product
              </button>
            </div>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><div className="spinner" /></div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc' }}>
                      {['Product', 'Price', 'Stock', 'Rating', 'Status', 'Actions'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '10px 16px', color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {products.length === 0 ? (
                      <tr><td colSpan={6} style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>No products yet. Add your first product!</td></tr>
                    ) : products.map(p => (
                      <tr key={p.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <img src={p.images?.[0] || `https://picsum.photos/seed/${p.id}/40/40`} alt={p.name} style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }} />
                            <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{p.name}</span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', fontWeight: 700, color: '#f43397' }}>₹{parseFloat(p.price).toFixed(0)}</td>
                        <td style={{ padding: '12px 16px', color: p.stock < 10 ? '#ef4444' : '#22c55e', fontWeight: 600 }}>{p.stock}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13 }}>⭐ {parseFloat(p.avg_rating || 0).toFixed(1)}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ background: p.is_active ? '#dcfce7' : '#fee2e2', color: p.is_active ? '#16a34a' : '#dc2626', padding: '3px 8px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                            {p.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => openEdit(p)} style={{ background: '#ede9fe', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#7c3aed' }}><Edit size={14} /></button>
                            <button onClick={() => handleDelete(p.id)} style={{ background: '#fee2e2', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#ef4444' }}><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'orders' && (
          <div style={{ background: 'white', borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Orders</h2>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Order ID', 'Amount', 'Payment', 'Status', 'Date'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '10px 16px', color: '#64748b', fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>No orders yet</td></tr>
                  ) : orders.map(o => (
                    <tr key={o.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '12px 16px', color: '#6c3fc5', fontWeight: 600, fontSize: 13 }}>#{o.id?.slice(0, 8).toUpperCase()}</td>
                      <td style={{ padding: '12px 16px', fontWeight: 700 }}>₹{parseFloat(o.total_amount || 0).toFixed(0)}</td>
                      <td style={{ padding: '12px 16px', fontSize: 13, textTransform: 'uppercase', color: '#64748b' }}>{o.payment_method}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ background: `${STATUS_COLORS[o.status]}20`, color: STATUS_COLORS[o.status], padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{o.status}</span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: '#94a3b8' }}>{new Date(o.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Product Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{editProduct ? 'Edit Product' : 'Add Product'}</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} color="#64748b" /></button>
            </div>
            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>Product Image</label>
                <ImageUpload value={form.images?.[0]} onChange={url => setForm(f => ({ ...f, images: [url] }))} />
              </div>
              {[
                { label: 'Product Name *', key: 'name' }, { label: 'Brand', key: 'brand' },
                { label: 'Price (₹) *', key: 'price', type: 'number' }, { label: 'MRP (₹)', key: 'mrp', type: 'number' },
                ...(editProduct ? [] : [{ label: 'Stock Quantity', key: 'stock', type: 'number' }]),
                { label: 'External Purchase Link (Amazon/Flipkart/etc.)', key: 'external_url', type: 'url' },
              ].map(({ label, key, type = 'text' }) => (
                <div key={key}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>{label}</label>
                  <input type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={{ width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ))}
              {editProduct && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', borderRadius: 10, padding: '10px 14px' }}>
                  <span style={{ fontSize: 13, color: '#374151' }}>Current stock: <strong>{editProduct.stock}</strong></span>
                  <button type="button" onClick={() => setAdjustProduct(editProduct)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#ede9fe', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: '#7c3aed', fontSize: 13, fontWeight: 600 }}>
                    <SlidersHorizontal size={14} /> Adjust Stock
                  </button>
                </div>
              )}
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>Category</label>
                <select value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))} style={{ width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}>
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 5 }}>Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} style={{ width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowModal(false)} style={{ padding: '10px 20px', border: '1px solid #e2e8f0', borderRadius: 10, background: 'white', cursor: 'pointer' }}>Cancel</button>
                <button onClick={handleSave} disabled={saving} style={{ padding: '10px 24px', background: '#f43397', color: 'white', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer' }}>
                  {saving ? 'Saving...' : editProduct ? 'Update' : 'Add Product'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {adjustProduct && (
        <AdjustStockModal
          product={adjustProduct}
          onClose={() => setAdjustProduct(null)}
          onSaved={(updated) => {
            setAdjustProduct(null);
            if (updated) setEditProduct(ep => (ep && ep.id === updated.id ? updated : ep));
            fetchData();
          }}
        />
      )}
    </div>
  );
}
